'use client';

import { Button, Icon, Typography } from '@workspace/ui/common';
import type { INavigationGroup, INavigationItem } from '../interfaces';
import { isNavigationGroup } from '../lib/header.utils';
import Link from 'next/link';
import { useNavigationGroup } from '../hooks';
import { cn, getLinkProps } from '@workspace/lib';
import { stripInvisibles } from '@workspace/constants';

interface INavigationGroupProps {
  group: INavigationGroup;
  index: number;
  activePath?: string;
  onActivePathChange?: (newPath: string) => void;
}

export const NavigationGroup = (props: INavigationGroupProps) => {
  const { group, index, activePath, onActivePathChange } = props;
  const cleanGroupTitle = stripInvisibles(group.groupTitle || '').trim();
  const {
    isMegaMenuOpen,
    handleNavigationClick,
    handleButtonClick,
    isActive,
    isMegaMenu,
    navigationGroups,
    navigationItems,
    items,
  } = useNavigationGroup({ group, activePath, onActivePathChange });

  // Render mega menu items dynamically from Sanity data
  const renderMegaMenuItems = () => {
    if (!isMegaMenu || !group.items) return null;

    if (group.groupTitle === 'Products') {
      // Products mega menu - dynamically generate columns from Sanity data

      return (
        <div className="w-full px-8 py-8">
          <div className="flex justify-center">
            <div className="flex gap-12 w-full wrapper">
              {/* Left side - All navigation content */}
              <div className="flex-1">
                {/* Navigation Groups - 5 columns */}
                <div className="grid grid-cols-4 gap-12 mb-12">
                  {navigationGroups.map((navGroup, colIndex) => (
                    <div key={colIndex}>
                      <Typography
                        variant="h3"
                        className="text-secondary-400 mb-4 text-left !no-underline !font-bold !text-xl !font-body min-w-max"
                      >
                        {navGroup.groupTitle}
                      </Typography>
                      <ul className="space-y-3 text-left">
                        {navGroup.items?.map((item: INavigationGroup | INavigationItem, idx: number) => (
                          <li key={idx}>
                            <Typography
                              variant="link"
                              {...getLinkProps(item.link)}
                              onClick={() => handleNavigationClick(item.link)}
                              className="text-gray-700 hover:text-primary-500 transition-colors duration-2000 block text-sm "
                            >
                              {isNavigationGroup(item) ? item.groupTitle : item.label}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                {/* Navigation Items - 5 columns */}
                {navigationItems.length > 0 && (
                  <div className="grid grid-cols-4 gap-12">
                    {/* Dynamically render all navigation items */}
                    {navigationItems.map((item: INavigationItem, index: number) => (
                      <div key={index}>
                        <Typography
                          variant="link"
                          {...getLinkProps(item.link)}
                          onClick={() => handleNavigationClick(item.link)}
                          className="text-secondary-400 hover:text-primary-500 transition-colors duration-2000 !no-underline !font-bold !text-xl !font-body !leading-8"
                        >
                          {item.label}
                        </Typography>
                      </div>
                    ))}

                    {/* Fill remaining columns with empty divs to maintain grid alignment */}
                    {Array.from({
                      length: Math.max(0, 5 - navigationItems.length),
                    }).map((_, index) => (
                      <div key={`empty-${index}`}></div>
                    ))}
                  </div>
                )}
              </div>

              {/* Right side - Advertising Banner */}
              <div className="flex-shrink-0 pl-25 border-l border-secondary-100">
                <div className="bg-secondary-200 rounded-lg flex items-center justify-center w-[282px] h-[282px]">
                  <Typography variant="bodySm" className="text-black">
                    Advertising Banner
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    } else if (group.groupTitle === 'Solutions') {
      // Solutions mega menu - dynamically generate grid from Sanity data
      return (
        <div className="w-full px-8 py-8">
          <div className="flex justify-center">
            <div className="grid grid-cols-3 gap-12 w-full max-w-6xl">
              {items.map((item: INavigationGroup | INavigationItem, idx: number) => (
                <div key={idx} className="text-center">
                  <div className="bg-secondary-200 h-32 rounded-lg mb-4 flex items-center justify-center">
                    <span className="text-black text-sm">Image</span>
                  </div>
                  <Typography variant="h3" className="font-semibold text-gray-900 mb-3 text-base">
                    {isNavigationGroup(item) ? item.groupTitle : item.label}
                  </Typography>
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    Lorem ipsum dolor sit amet, lorem ipsum dolor sit amet, lorem ipsum dolor.
                  </p>
                  <div className="flex justify-center">
                    <Icon name="arrowRightIcon" size={16} className="text-primary-500" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <li key={index} className="relative group">
      {group.link && (group.link.isOpenInNewTab || group.link.type === 'external') ? (
        <Link
          href={getLinkProps(group.link).href || '#'}
          target={getLinkProps(group.link).target}
          rel={getLinkProps(group.link).rel}
          className={cn(
            'transition-all cursor-pointer duration-300 font-semibold block !no-underline relative md:text-[16px] text-grey-500 px-[8px] py-[32px] leading-[20px]',
            isActive ? 'text-primary-500 font-medium' : 'text-black hover:text-primary-500',
          )}
        >
          {cleanGroupTitle}
        </Link>
      ) : (
        <Button
          onClick={handleButtonClick}
          className={cn(
            'transition-all cursor-pointer duration-300 font-semibold block !no-underline relative md:text-[16px] text-grey-500 px-[8px] py-[32px] leading-[20px]',
            isActive || isMegaMenuOpen ? 'text-primary-500 font-medium' : 'text-black hover:text-primary-500',
          )}
        >
          {cleanGroupTitle}
        </Button>
      )}

      {/* Mega Menu - only show on click */}
      {isMegaMenu && isMegaMenuOpen && (
        <div className="fixed top-[84px] w-screen left-0 right-0 bg-white shadow-xl border-t border-secondary-100 z-50 mega-menu-container">
          {renderMegaMenuItems()}
        </div>
      )}

      {/* Regular Dropdown Menu - only show on click */}
      {!isMegaMenu && group.items && group.items.length > 0 && isMegaMenuOpen && (
        <ul className="absolute top-full left-0 bg-white shadow-lg rounded-md py-2 min-w-[200px] z-50">
          {group.items.map((item, itemIndex) => (
            <li key={itemIndex}>
              <Link
                {...getLinkProps(item.link)}
                href={getLinkProps(item.link).href || '#'}
                onClick={() => handleNavigationClick(item.link)}
                className="text-gray-600 hover:text-primary-500 transition-colors duration-200 px-4 py-2 block"
              >
                {isNavigationGroup(item) ? item.groupTitle : item.label}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};
